import { LeftConfigurationTypeMeta } from '@omnea-app/packages/universal/conditionality/configuration/configuration'
import { Comparison, ConditionOperator, ConstantOperand, FormQuestionOperand, LeftOperand } from '../schema'

// ============================================================================
// Reference Types (moved from FE for shared use)
// ============================================================================

export type CoreDataReference = {
  coreDataType: string
  coreDataId: string
  type: 'core-data'
}

export type CustomFieldReference = {
  customFieldId: string
  optionId: string
  type: 'custom-field-choice'
}

export type ExternalReference = CoreDataReference | CustomFieldReference

// ============================================================================
// Resolved Operand Type
// ============================================================================

// ============================================================================
// Validation Variable Types
// ============================================================================

/**
 * Base validation variable - contains only validation-relevant fields.
 * FE's ComparisonVariable should extend this with UI-specific fields.
 */
export type BaseConditionVariable = {
  /** Unique identifier for matching against LHS operand */
  id: string
  /** The left-hand operand this variable represents */
  lhs: LeftOperand
  /** Resolved type for operator/RHS compatibility */
  resolvedType: LeftConfigurationTypeMeta
  /** Optional: override default operators for this resolved type */
  operatorOverrides: ConditionOperator[] | null
  /** Operators that are NOT valid for this variable */
  invalidOperators: ConditionOperator[]
}

/**
 * Variant for primitive types (number, boolean, entity).
 * Entity types (user, assignees) are primitive-like as they have no discrete options.
 */
export type PrimitiveConditionVariable = BaseConditionVariable & {
  resolvedType: Exclude<LeftConfigurationTypeMeta, 'string' | 'stringArray' | 'reference' | 'referenceArray'>
}

/**
 * Variant for reference types (core data, custom field choices).
 */
export type ReferenceConditionVariable = BaseConditionVariable & {
  resolvedType: 'reference' | 'referenceArray'
  /** Reference descriptor for the type of reference */
  reference: Omit<CustomFieldReference, 'optionId'> | Omit<CoreDataReference, 'coreDataId'>
  additionalOptions: ConditionVariableDiscreteOption[]
}

export type ConditionVariableDiscreteOption = { rhs: FormQuestionOperand | ConstantOperand }

/**
 * Variant for string types with optional discrete choices.
 */
export type StringConditionVariable = BaseConditionVariable & {
  resolvedType: 'string' | 'stringArray'
  /** Valid choice values (e.g., dropdown options). Null means free-form input. */
  discreteOptions: ConditionVariableDiscreteOption[] | null
}

/**
 * Union of all validation variable types.
 */
export type ConditionVariable = PrimitiveConditionVariable | ReferenceConditionVariable | StringConditionVariable

// ============================================================================
// Type Guards for ConditionVariable
// ============================================================================

export const isStringConditionVariable = (variable: ConditionVariable | null): variable is StringConditionVariable => {
  return variable != null && (variable.resolvedType === 'string' || variable.resolvedType === 'stringArray')
}

export const isReferenceConditionVariable = (variable: ConditionVariable | null): variable is ReferenceConditionVariable => {
  return variable != null && (variable.resolvedType === 'reference' || variable.resolvedType === 'referenceArray')
}
// ============================================================================
// Validation Error Types
// ============================================================================

/**
 * Field location within a single comparison.
 */
export type ComparisonField = 'lhs' | 'operator' | 'rhs'

/**
 * Granular error codes for each validation layer.
 */
export const VALIDATION_ERROR_CODES = [
  'empty-comparison',
  'unsupported-comparison',
  // Syntactic layer - schema/completeness
  'missing-lhs',
  'missing-operator',
  'missing-rhs',
  'invalid-lhs',
  'invalid-operator',
  'invalid-rhs',
  // Compatibility layer - type/operator matching
  'operator-incompatible-with-lhs',
  'rhs-type-incompatible',
  // Contextual layer - variable resolution
  'unknown-variable',
  'unknown-condition-group',
  'empty-condition-group',
  'invalid-condition-group',
  'broken-condition-group',
  // Consistency layer - reference resolution
  'unknown-reference',
  'archived-reference',
  'invalid-choice',
  'invalid-constant-format',
  'mismatch-reference',
] as const

export type ValidationErrorCode = (typeof VALIDATION_ERROR_CODES)[number]

/**
 * Location of the error within a condition tree.
 */
export type ValidationErrorLocation = {
  /** Path from root: array of indices for nested comparisons */
  path: number[]
  /** Which field in the comparison has the error */
  field: ComparisonField
  /** For multi-value RHS, which index has the error */
  rhsIndex?: number
}

/**
 * A single validation error with full context.
 */
export type ValidationError = {
  code: ValidationErrorCode
  location: ValidationErrorLocation
  message: string
  /** What the condition would evaluate to if broken (for AND/OR ignore logic) */
  outcome: boolean
  details?: {
    actualValue?: unknown
    expectedType?: string
    unresolvedId?: string
    /**
     * For condition-group errors, the errors found inside the group. The outer code collapses its
     * cause, so a caller comparing two validation runs cannot otherwise tell a group that broke for
     * a new reason from one that was already broken.
     */
    groupErrors?: ValidationError[]
  }
}

// ============================================================================
// Validation Result
// ============================================================================

/**
 * Result of validating a condition tree.
 */
export type ValidationResult = {
  errors: ValidationError[]
}

// ============================================================================
// Validation Context
// ============================================================================

/**
 * Pre-resolved reference data for pure function validation.
 * Key format: `{source}:{id}` for core data, `cf:{customFieldId}:{optionId}` for custom fields.
 */
export type ResolvedReference = {
  isArchived?: boolean
}
export type ResolvedReferenceMap = Map<string, ResolvedReference>

/**
 * Condition group resolution for VARIABLE_CONDITION_GROUP operands.
 */
export type ConditionGroupInfo = {
  id: string
  condition: Comparison | null
}

/**
 * Context required for validation.
 * Separates the "what to validate" from the "what's available".
 */
export type ValidationContext = {
  /** Available variables that can be used as LHS */
  variables: ConditionVariable[]
  /** Available condition groups */
  conditionGroups: ConditionGroupInfo[]
  /** Pre-resolved reference data */
  resolvedReferences: ResolvedReferenceMap
}
